import { TranslateService } from '@ngx-translate/core';
import { DeviceAndBrowserDetectorService } from 'src/app/services/device-and-browser-detector.service';
import { DeviceState } from 'src/app/models/device-manger';
import { DeviceType } from '../devices-handle-util';
import {
  DeviceErrorHelperModelBase,
  DeviceErrorHelperStep,
} from './device-error-helper-model-base';

/**
 * Explains that Firefox and Safari do not expose per-output device selection in the app;
 * users must change the system default speaker / sound output instead.
 * Copy is aligned to Firefox-on-Windows and Safari-on-Mac product mockups (step text and titles).
 */
export class SpeakerSystemOutputHelper extends DeviceErrorHelperModelBase {
  private _steps: DeviceErrorHelperStep[] = [];

  constructor(
    protected deviceDetector: DeviceAndBrowserDetectorService,
    protected translateService: TranslateService,
    protected deviceType: DeviceType,
  ) {
    super(translateService, deviceDetector, deviceType);
  }

  getSteps(): DeviceErrorHelperStep[] {
    this._steps = [];
    this.buildDesktopSteps();
    return this._steps;
  }

  getMobileTitle(): string {
    return this.translateService.instant('Change speakers in this browser');
  }

  getDesktopTitle(): string {
    if (this.deviceDetector.isWindows()) {
      return this.translateService.instant('Change speakers in Windows');
    }
    if (this.deviceDetector.isMacOS()) {
      return this.translateService.instant('Change speakers in MacOS');
    }
    return this.translateService.instant('Change speakers in this browser');
  }

  hasRelatedError(_cam: DeviceState, _mic: DeviceState): boolean {
    return false;
  }

  showRefreshButton(): boolean {
    return false;
  }

  getErrorMsg(_cam: DeviceState, _mic: DeviceState): string {
    return '';
  }

  getDeviceSettingsButtonLabel(): string {
    if (this.deviceDetector.isWindows()) {
      return this.translateService.instant('Open Settings');
    }
    if (this.deviceDetector.isMacOS()) {
      return this.translateService.instant('Open System Settings');
    }
    return super.getDeviceSettingsButtonLabel();
  }

  deviceSettingButtonSrc(): string {
    if (this.deviceDetector.isMacOS()) {
      return 'x-apple.systempreferences:com.apple.preference.sound';
    }
    if (this.deviceDetector.isWindows()) {
      return 'ms-settings:sound';
    }
    return '';
  }

  protected showRealDeviceSettingsButtonByGuide(): boolean {
    return this.deviceDetector.isMacOS() || this.deviceDetector.isWindows();
  }

  private buildWindowsDesktopSteps(): void {
    this._steps = [
      {
        stepText:
          `${this.translateService.instant('Open')} ` +
          `${this.strongHtml(this.translateService.instant('Settings'))} > ` +
          `${this.strongHtml(this.translateService.instant('System'))} > ` +
          `${this.strongHtml(this.translateService.instant('Sound'))}`,
        stepImgSrc: 'speaker-system-output-helper/windows-settings-sound-step-1.png',
      },
      {
        stepText: this.translateService.instant('Select your preferred speakers'),
        stepImgSrc: 'speaker-system-output-helper/windows-settings-sound-step-2.png',
      },
    ];
  }

  private buildMacDesktopSteps(): void {
    this._steps = [
      {
        stepText:
          `${this.translateService.instant('Open')} ` +
          `${this.strongHtml(this.translateService.instant('System Settings'))} > ` +
          `${this.strongHtml(this.translateService.instant('Sound'))}`,
        stepImgSrc: 'speaker-system-output-helper/macos-settings-sound-step-1.png',
      },
      {
        stepText: this.translateService.instant('Select your preferred speakers'),
        stepImgSrc: 'speaker-system-output-helper/macos-settings-sound-step-2.png',
      },
    ];
  }

  private buildDesktopSteps(): void {
    if (this.deviceDetector.isMacOS()) {
      this.buildMacDesktopSteps();
      return;
    }
    this.buildWindowsDesktopSteps();
  }
}
